"use client"

import { useMemo, useState, type ComponentProps } from "react"
import { Track } from "../client"
import {
  MicIcon,
  MicOffIcon,
  Loading03Icon,
  VideoIcon,
  VideoOffIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { Toggle } from "@workspace/ui/components/toggle"
import { cn } from "@workspace/ui/lib/utils"

function getSourceIcon(
  source: Track.Source,
  enabled: boolean,
  pending = false
) {
  if (pending) return Loading03Icon
  switch (source) {
    case Track.Source.Microphone:
      return enabled ? MicIcon : MicOffIcon
    case Track.Source.Camera:
      return enabled ? VideoIcon : VideoOffIcon
    default:
      return MicIcon
  }
}

export interface AgentTrackToggleProps
  extends Omit<ComponentProps<"button">, "value"> {
  source: "camera" | "microphone" | "screen_share"
  pending?: boolean
  pressed?: boolean
  defaultPressed?: boolean
  onPressedChange?: (pressed: boolean) => void
}

export function AgentTrackToggle({
  source,
  pending = false,
  pressed,
  defaultPressed = false,
  className,
  onPressedChange,
  ...props
}: AgentTrackToggleProps) {
  const [uncontrolledPressed, setUncontrolledPressed] = useState(
    defaultPressed ?? false
  )
  const isControlled = pressed !== undefined
  const resolvedPressed = useMemo(
    () => (isControlled ? pressed : uncontrolledPressed) ?? false,
    [isControlled, pressed, uncontrolledPressed]
  )

  const IconComponent = getSourceIcon(
    source as Track.Source,
    resolvedPressed,
    pending
  )

  const handlePressedChange = (nextPressed: boolean) => {
    if (!isControlled) setUncontrolledPressed(nextPressed)
    onPressedChange?.(nextPressed)
  }

  return (
    <Toggle
      pressed={isControlled ? pressed : undefined}
      defaultPressed={isControlled ? undefined : defaultPressed}
      aria-label={`Toggle ${source}`}
      onPressedChange={handlePressedChange}
      className={cn(
        "size-9 rounded-lg",
        "data-[state=on]:bg-muted data-[state=on]:text-foreground",
        "data-[state=off]:bg-destructive/10 data-[state=off]:text-destructive",
        className
      )}
      {...props}
    >
      <HugeiconsIcon
        icon={IconComponent}
        className={cn(pending && "animate-spin")}
      />
      {props.children}
    </Toggle>
  )
}
